import type { PayloadAction } from '@reduxjs/toolkit';
import type { EDSV2ArrayResponse } from '@/types/eds/api/responses/EDSV2Response';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { DocumentGenerationUpdateEntityPayload } from '@/domains/documents/store/types/DocumentGenerationUpdateEntityPayload';
import type { TemplateDocumentGenerationPayload } from '@/domains/documents/store/types/TemplateDocumentGenerationPayload';
import type { UploadDocumentGenerationPayload } from '@/domains/documents/store/types/UploadDocumentGenerationPayload';
import type { SelectOption } from '@/features/editMode/types/selectOption';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import { selectCurrentClaimSetId } from '@/domains/claimSets/store/selectors/claimSetSliceSelectors';
import { DocumentEntityType } from '@/domains/documents/enums/DocumentEntityType';
import DocumentEvents from '@/domains/documents/events';
import {
  generateDocumentGenerationTemplate,
  getDocumentGenerationClaimOptions,
  getDocumentGenerationClaimOptionsSuccess,
  getDocumentGenerationClaimantOptions,
  getDocumentGenerationClaimantOptionsSuccess,
  getDocumentOptionsFailed,
  resetDocumentGeneration,
  uploadDocumentGeneration,
} from '@/domains/documents/store/slices/generateDocumentSlice/generateDocumentSlice';
import { FileContentType } from '@/enums/FileContentType';
import { DocumentCreationService } from '@/services/DocumentCreationService/DocumentCreationService';
import { DocumentService } from '@/services/DocumentService/DocumentService';
import EventService from '@/services/EventService/EventService';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

function dispatchUpdatedEntityEvent({
  claimSetId,
  documentGeneration,
  documentUpload,
}: DocumentGenerationUpdateEntityPayload) {
  try {
    if (documentUpload) {
      const { entityType, entityKey } = documentUpload;

      if (entityType === DocumentEntityType.ClaimSet && entityKey !== null)
        return EventService.dispatch(
          new DocumentEvents.DocumentCreated({
            entityType: DocumentEntityType.ClaimSet,
            entityKey: String(claimSetId),
          }),
        );
      else if (entityType === DocumentEntityType.Claim && entityKey !== null)
        return EventService.dispatch(
          new DocumentEvents.DocumentCreated({ entityType: DocumentEntityType.Claim, entityKey: String(entityKey) }),
        );
      else if (entityType === DocumentEntityType.Claimant && entityKey !== null)
        return EventService.dispatch(
          new DocumentEvents.DocumentCreated({ entityType: DocumentEntityType.Claimant, entityKey: String(entityKey) }),
        );
    }

    if (documentGeneration) {
      const { entityType, entityKey } = documentGeneration;

      if (entityType === DocumentEntityType.ClaimSet && entityKey !== null)
        return EventService.dispatch(
          new DocumentEvents.DocumentCreated({
            entityType: DocumentEntityType.ClaimSet,
            entityKey: String(claimSetId),
          }),
        );
      else if (entityType === DocumentEntityType.Claim && entityKey !== null)
        return EventService.dispatch(
          new DocumentEvents.DocumentCreated({ entityType: DocumentEntityType.ClaimSet, entityKey: String(entityKey) }),
        );
      else if (entityType === DocumentEntityType.Claimant && entityKey !== null)
        return EventService.dispatch(
          new DocumentEvents.DocumentCreated({ entityType: DocumentEntityType.Claimant, entityKey: String(entityKey) }),
        );
    }
  } catch (error) {
    console.error(error);
  }
}

function* uploadDocumentGenerationSaga({ payload }: PayloadAction<UploadDocumentGenerationPayload>) {
  try {
    yield put(setSystemLoadingStatus(true));
    const form = yield call(
      DocumentService.getDocumentForm,
      payload.documentType?.id ? Number(payload.documentType.id) : undefined,
      payload.documentUpload,
      payload.documentReference,
      payload.createNewInstance,
      payload.preferredExistingDocumentEntityId,
    );

    yield call(HttpService.post, `${payload.documentEntity.type}/${payload.documentEntity.id}/Document`, form, {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });
    payload.onSuccess();

    SnacksService.add({
      type: SnackType.Success,
      title: 'Document has been added successfully',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    const claimSetId = yield select(selectCurrentClaimSetId);

    dispatchUpdatedEntityEvent({
      claimSetId,
      documentUpload: {
        entityType: payload.documentEntity.type,
        entityKey: payload.documentEntity.id,
      },
    });

    if (payload.documentUpload.type !== FileContentType.PDF) {
      yield delay(1500); // wait for document conversion
    }
  } catch (error) {
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    yield put(resetDocumentGeneration());
    ModalService.close(Modals.DocumentGeneration);
  }
}

function* generateDocumentGenerationTemplateSaga({ payload }: PayloadAction<TemplateDocumentGenerationPayload>) {
  try {
    yield put(setSystemLoadingStatus(true));

    yield call(HttpService.post, `Document/generate`, payload, {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });
    payload.onSuccess?.();

    const claimSetId = yield select(selectCurrentClaimSetId);

    const documentCreationService = yield call(DocumentCreationService.createServiceInstance);

    SnacksService.add({
      type: SnackType.Success,
      title: 'Document has been generated successfully',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    dispatchUpdatedEntityEvent({
      claimSetId,
      documentGeneration: {
        entityType: documentCreationService.getDocumentEntityType(String(payload.documentTypeId)),
        entityKey: payload.entityKey,
      },
    });
  } catch (error) {
    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to save the update.',
      content: 'Please try again',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    yield put(resetDocumentGeneration());
    ModalService.close(Modals.DocumentGeneration);
  }
}

export function* getDocumentGenerationClaimOptionsSaga({ payload }: PayloadAction<string>) {
  try {
    const { data }: HttpServiceResponse<EDSV2ArrayResponse> = yield call(
      HttpService.get,
      `/v2/ClaimSet/${payload}?includeEntities=ClaimEntities`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data);

    const claimOptions = (normalizedResponse.entities.ClaimEntities as NormalizedV2Response[]).reduce(
      (acc, claimEntity) => {
        if (claimEntity?.virtualFields?.isDeleted) return acc;

        return [
          ...acc,
          { label: claimEntity.fields.claimNumber?.value?.toString() || '', value: claimEntity.id.toString() },
        ];
      },
      [] as SelectOption[],
    );

    if (data) {
      yield put(getDocumentGenerationClaimOptionsSuccess(claimOptions));
    }
  } catch (error) {
    console.error(error);
  } finally {
    yield put(getDocumentOptionsFailed());
  }
}

export function* getDocumentGenerationClaimantOptionsSaga({ payload }: PayloadAction<string>) {
  try {
    const { data } = yield call(
      HttpService.get,
      `/v2/ClaimSet/${payload}?includeEntities=ClaimEntities.Claimants`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data);

    const claimantOptions = (normalizedResponse.entities.ClaimEntities as NormalizedV2Response[]).flatMap(
      (claim) =>
        (claim.entities.Claimants as NormalizedV2Response[])?.map((claimant) => ({
          label: `${claimant.fields.claimantName?.value} (${claim.fields.claimNumber?.value || 'unknown'})`,
          value: claimant.id.toString(),
        })) || [],
    );

    if (data) {
      yield put(getDocumentGenerationClaimantOptionsSuccess(claimantOptions));
    }
  } catch (error) {
    console.error(error);
  } finally {
    yield put(getDocumentOptionsFailed());
  }
}

export function* generateDocumentWatcher() {
  yield takeLatest(generateDocumentGenerationTemplate.type, generateDocumentGenerationTemplateSaga);
  yield takeLatest(uploadDocumentGeneration.type, uploadDocumentGenerationSaga);
  yield takeLatest(getDocumentGenerationClaimOptions.type, getDocumentGenerationClaimOptionsSaga);
  yield takeLatest(getDocumentGenerationClaimantOptions.type, getDocumentGenerationClaimantOptionsSaga);
}
